// What the newsletter ask says, per article category.
//
// WHY THIS EXISTS. Every article carries two email-capture surfaces already
// (the end-of-read card and the sticky rail), on all 510 published pieces, and
// both said the same thing to everybody: "Notes on system design and shipping
// AI, for engineers moving from senior to staff". A reader who searched their
// way onto a CSS post or an SQL exercise is being offered something with no
// relationship to why they came, and the numbers say so: roughly 800 real
// views a week converting at 0.3%.
//
// So the ask is matched to the page. This is copy, not targeting: there is ONE
// list and ONE newsletter, and each pitch below has to stay true of it. The
// category picks the door in, not the product behind it. Promising a
// CSS-only newsletter here would be a lie the first send has to pay for.
//
// WHAT IS DELIBERATELY NOT CHANGED. The `?src=` tags on both surfaces stay
// exactly as they were (`article:<slug>` and `article-rail:<slug>`), because
// the only baseline that exists is the same tag before and after this change.
// Retagging would throw the comparison away to gain nothing.
//
// Keep the lines short. The rail renders them in a narrow sticky column, so a
// long line turns into six wrapped rows next to the table of contents.

export interface SubscribePitch {
  /** Card heading. The rail keeps its own "Newsletter" overline. */
  heading: string
  /** One line, both surfaces. Must stay true of the actual newsletter. */
  line: string
}

/**
 * The generic ask. Used where there is no category (the /learnings block) and
 * as the fallback for any category not listed below, so a new category ships
 * with working copy rather than a blank.
 */
export const DEFAULT_PITCH: SubscribePitch = {
  heading: 'Get the occasional email',
  line: "Notes on system design and shipping AI, delivered when there's something worth keeping. No fixed schedule, no fluff.",
}

/**
 * The ask on a podcast episode page. Episodes have free-text tags rather than a
 * category, and the whole show is one topic, so one pitch covers all of them.
 *
 * Careful with this one: it must not imply "subscribe and I'll email you when
 * an episode lands", because nothing sends that. It offers the same list the
 * rest of the site offers, framed for someone who just watched.
 */
export const PODCAST_PITCH: SubscribePitch = {
  heading: 'More like this episode?',
  line: 'Occasional notes on system design and shipping AI, from the engineer who records these.',
}

/**
 * The ask at the foot of the homepage. The homepage reader arrived at the front
 * door rather than through a topic, so unlike an article pitch this one cannot
 * lean on "more like the thing you just read": it has to say who the list is
 * for. The wording is the promise /newsletter already makes, word for word, so
 * the two surfaces cannot drift into offering different things.
 */
export const HOME_PITCH: SubscribePitch = {
  heading: 'Get the occasional email',
  line: 'Notes on system design and shipping AI, written for engineers moving from senior to staff. No fixed schedule, no fluff.',
}

/**
 * One pitch per category. Covers all 16 categories carrying traffic as at
 * 7 Aug 2026; the accent map in `utils/categoryAccent.ts` is the same list, and
 * the two should be kept in step.
 */
export const CATEGORY_PITCH: Record<string, SubscribePitch> = {
  'system-design': {
    heading: 'More system design like this?',
    line: 'Occasional notes on designing systems that survive production, and defending the trade-offs.',
  },
  typescript: {
    heading: 'More TypeScript like this?',
    line: 'Occasional notes on TypeScript in real codebases, and the patterns I keep deleting in review.',
  },
  reviews: {
    heading: 'More book notes like this?',
    line: "Occasional notes on what I'm reading, and the parts that actually changed how I work.",
  },
  devops: {
    heading: 'More tooling comparisons?',
    line: 'Occasional notes on build tooling and pipelines, and how to pick between them honestly.',
  },
  leadership: {
    heading: 'Moving from senior to staff?',
    line: 'Occasional notes on scope, influence, and the work that gets you promoted rather than just busy.',
  },
  algorithm: {
    heading: 'More algorithms like this?',
    line: 'Occasional notes on the algorithms that actually come up, in code review and in interviews.',
  },
  'artificial-intelligence': {
    heading: 'Shipping AI to production?',
    line: 'Occasional notes on building LLM and agent systems that hold up outside a demo.',
  },
  'clean-code': {
    heading: 'More on code quality?',
    line: 'Occasional notes on writing code the next person can change without fear.',
  },
  'html-css': {
    heading: 'More frontend like this?',
    line: 'Occasional notes on CSS and browser behaviour, and why the layout does that.',
  },
  'design-pattern': {
    heading: 'More patterns like this?',
    line: 'Occasional notes on design patterns, and when reaching for one makes things worse.',
  },
  sql: {
    heading: 'More SQL like this?',
    line: 'Occasional notes on SQL, from practice exercises to the plan the database actually runs.',
  },
  'data-structure': {
    heading: 'More data structures?',
    line: 'Occasional notes on the data structures worth knowing, and the ones interviews keep asking for.',
  },
  database: {
    heading: 'More on databases?',
    line: 'Occasional notes on schema design, indexes, and the queries that get slow at scale.',
  },
  testing: {
    heading: 'More on testing?',
    line: 'Occasional notes on the tests worth writing, and the ones that only slow the build down.',
  },
  security: {
    heading: 'More on security?',
    line: 'Occasional notes on threat modelling, and the failure modes that actually get exploited.',
  },
  accessibility: {
    heading: 'More on accessibility?',
    line: 'Occasional notes on building interfaces that work for everyone, tested rather than assumed.',
  },
}

/** The ask for a category, falling back to the generic one. */
export function subscribePitch(category?: string): SubscribePitch {
  return (category && CATEGORY_PITCH[category.toLowerCase()]) || DEFAULT_PITCH
}
